import React from "react";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { PRIMARY_ORANGE } from "./constants";

const getPageNumbers = (page, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }
  const pages = new Set([0, page, totalPages - 1]);
  if (page > 0) pages.add(page - 1);
  if (page < totalPages - 1) pages.add(page + 1);
  if (page > 1) pages.add(page - 2);
  if (page < totalPages - 2) pages.add(page + 2);

  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  sorted.forEach((p, idx) => {
    if (idx > 0 && p - sorted[idx - 1] > 1) result.push("ellipsis");
    result.push(p);
  });
  return result;
};

const DataTablePagination = ({
  count = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
  entityLabel = "items",
}) => {
  const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);
  const pageNumbers = getPageNumbers(page, totalPages);

  return (
    <Box
      className="data-table-pagination"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        mt: 2,
        px: 0.5,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        Showing {from} to {to} of {count} {entityLabel}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <IconButton
          size="small"
          disabled={page === 0}
          onClick={() => onPageChange?.(null, page - 1)}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        {pageNumbers.map((item, index) =>
          item === "ellipsis" ? (
            <Typography key={`ellipsis-${index}`} sx={{ px: 0.5, color: "#999" }}>
              ...
            </Typography>
          ) : (
            <Box
              key={item}
              component="button"
              type="button"
              onClick={() => onPageChange?.(null, item)}
              sx={{
                minWidth: 32,
                height: 32,
                border:
                  item === page
                    ? `1px solid ${PRIMARY_ORANGE}`
                    : "1px solid transparent",
                borderRadius: "6px",
                bgcolor: "transparent",
                color: item === page ? PRIMARY_ORANGE : "text.primary",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: item === page ? 600 : 400,
              }}
            >
              {item + 1}
            </Box>
          )
        )}

        <IconButton
          size="small"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange?.(null, page + 1)}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>

      <FormControl size="small" sx={{ minWidth: 110 }}>
        <Select
          value={rowsPerPage}
          onChange={onRowsPerPageChange}
          displayEmpty
          sx={{ fontSize: "0.85rem", borderRadius: "8px" }}
        >
          {rowsPerPageOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option} / page
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default DataTablePagination;
